import {
  Component,
  OnDestroy,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
  Inject,
  Input,
} from '@angular/core'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { Observable, take } from 'rxjs'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { ClusteredSignProperties } from 'src/app/shared/models/clustered-sign-model'
import { MapSignsIcons, MapSupsIcons } from 'src/app/core/enums/map-icons.enum'
import { MapService } from 'src/app/core/services/map.service'
import maplibregl from 'maplibre-gl'
import { SpritePositionService } from 'src/app/core/services/sprite-position.service'
import { HttpClient } from '@angular/common/http'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { MapSignsDisplayTypes } from 'src/app/core/enums/sign-model'
import { ProviderInfo } from 'src/app/shared/models/provider-info.model'

@Component({
  selector: 'app-sign-simulation-dialog',
  templateUrl: './sign-simulation-dialog.component.html',
  standalone: false,
})
export class SignSimulationDialogComponent implements OnDestroy {
  @Input() providerInfo$!: Observable<ProviderInfo>

  public selectedFeatureId = ''
  public properties: ClusteredSignProperties | undefined
  public details = new Map<string, any>()
  public rawFeature: string | undefined
  public features: MapGeoJSONFeature[] = []
  public zoomLevel: number | undefined
  public index: number = 0
  public marker: any = null
  public popup: any = null
  public lastRightClick: { lng: number; lat: number } | null = null

  public signTypes = Object.values(MapSignsIcons)
  public signDisplayTypes = Object.values(MapSignsDisplayTypes)
  public supplementarySignTypes = Object.values(MapSupsIcons)
  public selectedSignType: string = this.signTypes[0]
  public selectedSignDisplayType: string = this.signDisplayTypes[0]
  public popupSignType: string = this.selectedSignType
  public popupSpeedValue: number = 50
  public popupBearing: number = 0
  public popupSupplementarySigns: string[] = []
  public sourceOptions: string[] = []
  public popupSource = ''

  @ViewChild('popupTemplate', { static: true }) popupTemplate!: TemplateRef<any>

  // For map rotation compensation
  private mapEventListener: (() => void) | null = null
  private boundMapRightClick: any

  constructor(
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
    private readonly mapEventsService: MapEventsService,
    private MapService: MapService,
    private spritePositionService: SpritePositionService,
    private viewContainerRef: ViewContainerRef,
    private http: HttpClient,
  ) {
    this.details = new Map<string, any>()
    this.boundMapRightClick = (ev: any) => this.handleMapRightClick(ev)
    this.mapEventsService.registerMapContextMenu(this.boundMapRightClick)
  }

  ngOnInit() {
    this.providerInfo$.pipe(take(1)).subscribe((providerInfo) => {
      this.sourceOptions = [providerInfo.provider1, providerInfo.provider2]
      this.popupSource = this.sourceOptions[0] ?? ''
    })
  }

  handleMapRightClick(ev: { lngLat: { lng: number; lat: number }; originalEvent: MouseEvent }) {
    this.lastRightClick = { lng: ev.lngLat.lng, lat: ev.lngLat.lat }

    // Remove previous marker and popup if they exist
    if (this.marker) {
      this.marker.remove()
      this.marker = null
    }
    if (this.popup) {
      this.popup.remove()
      this.popup = null
    }
    this.unregisterMapRotationListener()

    // Create embedded view for the popup
    const viewRef = this.viewContainerRef.createEmbeddedView(this.popupTemplate)
    viewRef.detectChanges()
    const popupContent = viewRef.rootNodes[0]

    // Show marker for initial state
    this.createMarker(ev.lngLat, this.getPopupIconName(), this.popupBearing)

    // Show popup and remove marker when popup closes
    this.popup = new maplibregl.Popup({ offset: 25, closeButton: false })
      .setLngLat([ev.lngLat.lng, ev.lngLat.lat])
      .setDOMContent(popupContent)
      .addTo(this.MapService.map)

    this.popup.on('close', () => {
      if (this.marker) {
        this.marker.remove()
        this.marker = null
      }
      this.popup = null
      this.unregisterMapRotationListener()
    })
  }

  canBeDigital(): boolean {
    return (
      this.popupSignType.startsWith('SPEED') ||
      this.popupSignType === MapSignsIcons.ALL_RESTRICTIONS_END
    )
  }

  isZoneSign(): boolean {
    return (
      this.popupSignType === MapSignsIcons.SPEED_LIMIT_ZONE ||
      this.popupSignType === MapSignsIcons.SPEED_LIMIT_ZONE_END
    )
  }

  onPopupSignTypeChange() {
    if (this.isZoneSign()) {
      this.popupSpeedValue = 20
    } else {
      this.popupSpeedValue = 50
    }
    this.selectedSignDisplayType = MapSignsDisplayTypes.ANALOG
    this.createMarker(this.lastRightClick!, this.getPopupIconName(), this.popupBearing)
  }

  onPopupSpeedChange() {
    this.createMarker(this.lastRightClick!, this.getPopupIconName(), this.popupBearing)
  }

  onPopupBearingChange() {
    this.createMarker(this.lastRightClick!, this.getPopupIconName(), this.popupBearing)
  }

  onPopupDisplayTypeChange() {
    this.createMarker(this.lastRightClick!, this.getPopupIconName(), this.popupBearing)
  }

  // Add this method to handle changes in supplementary signs
  onPopupSupplementaryChange() {
    this.createMarker(
      this.lastRightClick!,
      this.getPopupIconName(),
      this.popupBearing,
      this.popupSupplementarySigns,
    )
  }

  getPopupIconName(): string {
    if (this.popupSignType.startsWith('SPEED_LIMIT')) {
      return this.selectedSignDisplayType === MapSignsDisplayTypes.DIGITAL
        ? `${this.popupSignType}_${this.popupSpeedValue}_DIGITAL`
        : `${this.popupSignType}_${this.popupSpeedValue}`
    }
    return this.selectedSignDisplayType === MapSignsDisplayTypes.DIGITAL
      ? `${this.popupSignType}_DIGITAL`
      : this.popupSignType
  }

  // Helper to create a marker with the selected icon and bearing, compensating for map rotation
  private async createMarker(
    lngLat: { lng: number; lat: number },
    signType: string,
    bearing: number = 0,
    supplementarySigns: string[] = [],
  ) {
    if (this.marker) {
      this.marker.remove()
      this.marker = null
    }

    const spriteData = await this.spritePositionService.getSpritePosition(signType, 'medium')
    if (!spriteData) {
      console.error('Sprite data not found for sign type:', signType)
      return
    }

    // Outer marker element
    const el = document.createElement('div')
    el.style.width = `${spriteData.width}px`
    el.style.height = `${spriteData.height}px`
    el.style.display = 'flex'
    el.style.alignItems = 'center'
    el.style.justifyContent = 'center'

    // Inner icon element (rotated)
    const icon = document.createElement('div')
    icon.style.width = '100%'
    icon.style.height = '100%'
    icon.style.backgroundImage = `url('${spriteData.spriteUrl}')`
    icon.style.backgroundPosition = `-${spriteData.x}px -${spriteData.y}px`
    icon.style.backgroundSize = `${spriteData.spriteWidth}px ${spriteData.spriteHeight}px`
    icon.style.backgroundRepeat = 'no-repeat'
    icon.style.display = 'block'
    icon.style.transformOrigin = 'center center'

    el.appendChild(icon)

    this.marker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat([lngLat.lng, lngLat.lat])
      .addTo(this.MapService.map)

    this.registerMapRotationListener(icon, bearing)
    this.updateMarkerRotation(icon, bearing)
  }

  // Update marker rotation to compensate for map bearing
  private updateMarkerRotation(icon: HTMLElement, userBearing: number) {
    const mapBearing = this.MapService.map.getBearing ? this.MapService.map.getBearing() : 0
    icon.style.transformOrigin = 'center center'
    icon.style.transform = `rotate(${userBearing - mapBearing}deg)`
  }

  // Listen for map rotation/move to update marker rotation
  private registerMapRotationListener(icon: HTMLElement, userBearing: number) {
    this.unregisterMapRotationListener()
    this.mapEventListener = () => {
      this.updateMarkerRotation(icon, userBearing)
    }
    this.MapService.map.on('rotate', this.mapEventListener)
    this.MapService.map.on('move', this.mapEventListener)
  }

  private unregisterMapRotationListener() {
    if (this.mapEventListener) {
      this.MapService.map.off('rotate', this.mapEventListener)
      this.MapService.map.off('move', this.mapEventListener)
      this.mapEventListener = null
    }
  }

  ngOnDestroy() {
    if (this.marker) this.marker.remove()
    if (this.popup) this.popup.remove()
    this.unregisterMapRotationListener()
    this.mapEventsService.detachMapContextMenu(this.boundMapRightClick)
  }

  simulateObservation() {
    let simObservationFeature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [this.lastRightClick!.lng, this.lastRightClick!.lat],
      },
      properties: {
        submitter: 'simulation-tool',
        source: this.popupSource,
        probability: 1.0,
        heading: this.popupBearing,
        width: 2,
        height: 2,
        mainType: this.popupSignType,
        speedLimit: this.popupSpeedValue,
        supplementalSigns: this.popupSupplementarySigns.map((type) => {
          return { supType: type, value: '' }
        }),
        displayType: this.selectedSignDisplayType,
      },
    }
    this.http.put(this.config.backend_url + 'vzo/observations', simObservationFeature).subscribe({
      next: (response) => {
        console.log('Observation sent successfully:')
        this.popup?.remove()
      },
      error: (err) => {
        console.error('Failed to send observation:', err)
      },
    })
  }

  removeSupplementarySign(sup: string) {
    this.popupSupplementarySigns = this.popupSupplementarySigns.filter((s) => s !== sup)
    this.onPopupSupplementaryChange()
  }
}
